<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <div class="card-icon">
            <md-icon>group</md-icon>
          </div>
          <h4 class="title">Customers</h4>
        </md-card-header>
        <md-card-content>
          <md-table v-model="customers" @md-selected="onSelect">
            <md-table-toolbar style="align-items:flex-start">
              <div class="md-toolbar-section-start">
                <div class="md-layout">
                  <div class="md-layout-item md-size-70 no-padding">
                    <md-field id="customer-search-input">
                      <form v-on:submit.prevent="findCustomer">
                        <md-input
                          placeholder="Search for Customers ..."
                          v-model="criteria"
                          ref="search"
                        />
                      </form>
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-30">
                    <md-button
                      class="md-icon-button md-dense md-primary md-round"
                      @click="findCustomer"
                    >
                      <md-icon>search</md-icon>
                      <md-tooltip md-direction="top"
                        >Search Customers</md-tooltip
                      >
                    </md-button>
                    <md-button
                      v-if="hasCriteria"
                      class="md-icon-button md-dense md-danger md-round"
                      style="margin-left: 10px;"
                      @click="clearResults"
                    >
                      <md-icon>clear</md-icon>
                      <md-tooltip md-direction="top"
                        >Clear Search Results</md-tooltip
                      >
                    </md-button>
                  </div>
                </div>
              </div>
              <div class="md-toolbar-section-end">
                <div class="md-layout">
                  <div class="md-layout-item md-size-100 text-right no-padding">
                    <md-button
                      class="md-icon-button md-dense md-primary md-round"
                      @click="newCustomer"
                    >
                      <md-icon>add</md-icon>
                      <md-tooltip md-direction="top"
                        >Add New Customer</md-tooltip
                      >
                    </md-button>
                  </div>
                </div>
              </div>
            </md-table-toolbar>

            <md-table-empty-state
              md-label="Nothing to Show"
              :md-description="
                `No Customers to show. Enter your Search Criteria above or add a new Customer.`
              "
            >
              <md-button class="md-primary md-raised" @click="newCustomer"
                >Add New Customer</md-button
              >
            </md-table-empty-state>

            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="single"
            >
              <md-table-cell md-label="First Name">{{
                item.first_name
              }}</md-table-cell>
              <md-table-cell md-label="Last Name">{{
                item.last_name
              }}</md-table-cell>
              <md-table-cell md-label="Email">
                <a :href="`mailto:${item.email}`">{{ item.email }}</a>
              </md-table-cell>
              <md-table-cell md-label="Manager">
                {{ item.manager.full_name }}
              </md-table-cell>
              <md-table-cell class="text-right">
                <md-button
                  :to="{ name: 'Customer Profile', params: { id: item.id } }"
                  class="md-just-icon md-simple md-success"
                >
                  <md-icon>visibility</md-icon>
                  <md-tooltip md-direction="top">View</md-tooltip>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>

    <md-dialog :md-active.sync="showDialog">
      <md-dialog-title>New Customer</md-dialog-title>

      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(createCustomer)">
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-20">
              <md-field>
                <label for="title">Title</label>
                <md-select v-model="customer.title" name="title" id="title">
                  <md-option value="Mr">Mr</md-option>
                  <md-option value="Mrs">Mrs</md-option>
                  <md-option value="Miss">Miss</md-option>
                  <md-option value="Ms">Ms</md-option>
                  <md-option value="Dr">Dr</md-option>
                  <md-option value="Prof">Prof</md-option>
                  <md-option value="Rev">Rev</md-option>
                </md-select>
              </md-field>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-40">
              <ValidationProvider
                name="First Name"
                rules="required"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <label>First Name</label>
                  <md-input
                    v-model="customer.first_name"
                    type="text"
                  ></md-input>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-40">
              <ValidationProvider
                name="Last Name"
                rules="required"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <label>Last Name</label>
                  <md-input v-model="customer.last_name" type="text"></md-input>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
            <div class="md-layout-item md-small-size-100 md-size-100">
              <ValidationProvider
                name="Email"
                rules="required|email"
                v-slot="{ passed, failed }"
              >
                <md-field
                  :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
                >
                  <label>Email Address</label>
                  <md-input v-model="customer.email" type="email"></md-input>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
          </div>
          <md-dialog-actions>
            <md-button class="md-primary" @click="cancel">Cancel</md-button>
            <md-button type="submit" class="md-primary">Create</md-button>
          </md-dialog-actions>
        </form>
      </ValidationObserver>
    </md-dialog>
  </div>
</template>

<script>
import customerApi from "@/services/api/customer";
import { SlideYDownTransition } from "vue2-transitions";
import { extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";

extend("email", email);
extend("required", required);

export default {
  components: {
    SlideYDownTransition
  },

  data() {
    return {
      customers: [],
      criteria: null,
      showDialog: false,
      customer: {
        title: null,
        first_name: null,
        last_name: null,
        email: null
      }
    };
  },

  computed: {
    hasCriteria() {
      return !!this.criteria;
    }
  },

  methods: {
    findCustomer() {
      customerApi.findCustomer(this.criteria).then(response => {
        this.customers = response.data.customers;
        if (!this.customers.length)
          this.$notify({
            timeout: 2500,
            message: "Hmmm. We couldn't find any matches.",
            icon: "cancel",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "warning"
          });
      });
    },

    onSelect(item) {
      this.$router.push({ name: "Customer Profile", params: { id: item.id } });
    },

    newCustomer() {
      this.showDialog = true;
    },

    cancel() {
      this.showDialog = false;
    },

    clearResults() {
      this.customers = [];
      this.criteria = null;
      this.focusInput();
    },

    createCustomer() {
      customerApi.createCustomer(this.customer).then(response => {
        this.$router.push(`/customer/${response.data.id}`);
      });
    },

    focusInput() {
      this.$refs.search.$el.focus();
    }
  },

  mounted() {
    this.focusInput();
  },

  watch: {}
};
</script>
<style lang="scss" scoped>
.no-padding {
  padding: 0px;
}
.md-toolbar .md-field .md-input {
  margin-top: 0px;
}
.md-table-toolbar {
  margin-bottom: 20px;
  .md-toolbar-section-start {
    form,
    input {
      width: 100%;
    }
  }
}
</style>
